/**
 * ADR (PR-F, v1.4.6) — PiP module consolidation.
 *
 * The repo previously carried several PiP-related TS modules. PR-F settles the
 * surviving layering to a single stack so there is exactly one PiP code path:
 *
 *   nexPip3D.ts        — native Capacitor bridge to the one NexPip3D plugin
 *                        (AVKit controller, display layer, diagnostics). LOWEST.
 *   nexPip.ts (this)   — lifecycle + status façade over nexPip3D. The ONLY
 *                        module the app calls for start/stop/status, including
 *                        the Dex-scan phase copy folded in from the former
 *                        dexLivePip.ts (see "Dex-scan PiP surface" below).
 *   nexPip3DBridge.ts  — OCR→HUD clinical-overlay bridge (circles/arrow/screen),
 *                        consumed by LiveScanModal's frame loop.
 *
 * DELETED in PR-F: dexLivePip.ts (its 105-line phase→copy wrapper is now the
 * "Dex-scan PiP surface" section at the bottom of this file). The two NexPip 3D
 * *Lab* components and the standalone NexPip plugin JS path were already removed
 * in PR-E. Decision rationale: nexPip3D/nexPip/nexPip3DBridge are each
 * load-bearing for the production LiveScanModal; dexLivePip was the only true
 * thin duplicate and had a single importer (useDexLiveScan), so it folds in
 * cleanly without touching the modal.
 *
 * Nex companion status channel — v1.3.2 b1 UNIFIED BRIDGE.
 *
 * History: through 1.3.1 there were two independent native plugins
 * (`NexPip`, `NexPip3D`) each owning their own AVKit controller, host
 * view, sample-buffer display layer, audio session, and render loop.
 * LiveScanModal called `startPip()` on `NexPip` but then drove status
 * updates into `NexPip3D` via the OCR bridge. The plugin that was
 * actually promoted to PiP never received any status updates, while the
 * plugin that received updates was never started — which is why the
 * floating PiP surface was permanently black even though the lifecycle
 * logs showed `didStartPiP` / `active=true`.
 *
 * 1.3.2 fix: this file is now a thin shim. Every lifecycle call
 * (isPipSupported / preparePip / startPip / updatePipStatus / stopPip /
 * getPipDiagnostics / subscribePipState / subscribePipEvents) is
 * forwarded to the `NexPip3D` plugin so there is exactly ONE PiP path.
 * The legacy `NexPip` native plugin remains registered in
 * MyViewController.swift for API compatibility, but nothing in the JS
 * layer talks to it any more.
 *
 * Public TypeScript surface is preserved so LiveScanModal and any other
 * call sites keep compiling without churn.
 */

import { getPlatform } from "./platform";
import * as Pip3D from "./nexPip3D";
import { DEX_SCAN_VERBOSE } from "./dexScanLog";

export type NexPipPhase =
  | "idle"
  | "scanning"
  | "captured_threshold_reached"
  | "finished";

export interface NexPipStatus {
  phase: NexPipPhase;
  captured?: number;
  target?: number;
  detail?: string;
}

/**
 * Legacy diagnostic shape kept stable for existing call sites
 * (LiveScanModal, anything that uses describePipStatus). Adapted from
 * the NexPip3D diagnostics block.
 */
export interface PipDiagnostics {
  isSupported: boolean;
  isPossible: boolean;
  isActive: boolean;
  controllerExists: boolean;
  audioSessionActive: boolean;
  sampleBuffersRendered: number;
  sampleBuffersEnqueued: number;
  lastFrameEnqueueAt: number;
  lastStartAttemptAt: number;
  lastDelegateEvent: string;
  phase: string;
  captured: number;
  target: number;
  renderTimerActive: boolean;
  hostViewAttached: boolean;
  hostViewWidth: number;
  hostViewHeight: number;
  displayLayerStatus: number;
  displayLayerReady: boolean;
  autoInlineEnabled: boolean;
  lastError: string | null;
  event?: string;
  /** New in 1.3.2: surfaces which native plugin produced this snapshot. */
  pluginName?: string;
  /** New in 1.3.2: surfaces the current renderer mode. */
  rendererMode?: string;
}

const DEFAULT_TARGET = 12;

let _firedThisSession = false;
let _lastPostedAt = 0;
const POST_THROTTLE_MS = 400;

/** Translate a NexPip3D diagnostic into the legacy NexPip shape. */
function adapt3DtoLegacy(d: Pip3D.NexPip3DDiagnostics | null): PipDiagnostics | null {
  if (!d) return null;
  return {
    isSupported: d.isSupported,
    isPossible: d.isPossible,
    isActive: d.isActive,
    controllerExists: d.controllerExists,
    audioSessionActive: d.audioSessionActive,
    sampleBuffersRendered: d.framesRendered,
    sampleBuffersEnqueued: d.framesRendered,
    lastFrameEnqueueAt: d.lastFrameAt,
    lastStartAttemptAt: d.lastStartAttemptAt,
    lastDelegateEvent: d.lastDelegateEvent,
    phase: d.phase,
    captured: d.captured,
    target: d.target,
    renderTimerActive: d.displayLinkActive,
    hostViewAttached: d.hostViewAttached,
    hostViewWidth: d.hostViewWidth,
    hostViewHeight: d.hostViewHeight,
    displayLayerStatus: d.layerStatus,
    displayLayerReady: d.layerReady,
    autoInlineEnabled: d.autoInlineEnabled,
    lastError: d.lastError,
    event: d.event,
    pluginName: (d as unknown as { pluginName?: string }).pluginName ?? "NexPip3D",
    rendererMode: (d as unknown as { rendererMode?: string }).rendererMode,
  };
}

function legacyPhaseToPip3D(phase: NexPipPhase | string): Pip3D.NexPip3DPhase | string {
  // The 3D plugin understands all legacy phase strings as-is plus its
  // own extended traffic-light vocabulary. Anything else is forwarded
  // verbatim and the native side falls back to a yellow HUD.
  return phase as Pip3D.NexPip3DPhase | string;
}

// ---------- Public PiP control surface ----------

export interface PipSupportInfo {
  supported: boolean;
  possible: boolean;
  reason?: string;
}

export async function isPipSupported(): Promise<PipSupportInfo> {
  if (!Pip3D.isAvailable()) {
    return {
      supported: false,
      possible: false,
      // No reason on WEB (2026-08-31): the "Start HUD" row already reads
      // UNSUPPORTED, so a sentence under it only repeated that — and the one
      // we had said "iOS app", which has been wrong since the HUD shipped on
      // Android.
      //
      // The native branch DOES surface a reason, and it is worded as user
      // copy rather than as a developer note: a trainer on a device whose
      // plugin failed to load sees this line, so it says "Nexus Dex HUD" like
      // every other user-facing mention (owner direction).
      reason:
        getPlatform() === "web"
          ? undefined
          : "The Nexus Dex HUD plugin is not available",
    };
  }
  return Pip3D.isSupported();
}

export async function preparePip(): Promise<{ prepared: boolean; reason?: string }> {
  if (!Pip3D.isAvailable()) {
    return { prepared: false, reason: "plugin not available" };
  }
  return Pip3D.prepare();
}

export interface StartPipResult {
  started: boolean;
  reason?: string;
  diagnostics?: PipDiagnostics;
}

export async function startPip(): Promise<StartPipResult> {
  if (!Pip3D.isAvailable()) {
    // eslint-disable-next-line no-console
    console.log("[live-scan] startPip → NexPip3D plugin not available");
    return { started: false, reason: "plugin not available" };
  }
  // eslint-disable-next-line no-console
  console.log("[live-scan] startPip → NexPip3D.start()");
  const res = await Pip3D.start();
  // eslint-disable-next-line no-console
  console.log("[live-scan] NexPip3D.start →", { started: res.started, reason: res.reason });
  return {
    started: res.started,
    reason: res.reason,
    diagnostics: adapt3DtoLegacy(res.diagnostics ?? null) ?? undefined,
  };
}

export async function updatePipStatus(status: NexPipStatus): Promise<boolean> {
  const milestone =
    status.phase === "captured_threshold_reached" ||
    status.phase === "finished";
  const now = Date.now();
  if (!milestone && now - _lastPostedAt < POST_THROTTLE_MS) return false;
  _lastPostedAt = now;

  if (Pip3D.isAvailable()) {
    const delivered = await Pip3D.updateStatus({
      phase: legacyPhaseToPip3D(status.phase),
      captured: status.captured,
      target: status.target,
      detail: status.detail,
    });
    return delivered;
  }

  // Web / non-iOS fallback retained from earlier builds.
  const platform = getPlatform();
  if (platform === "web") {
    // eslint-disable-next-line no-console
    console.info("[nexPip:web]", status);
    return true;
  }

  // No notification fallback (2026-09-11): a trainer mid-scan needs no
  // banner telling them so. Without the PiP HUD there is nothing to deliver.
  return false;
}

export async function stopPip(): Promise<boolean> {
  if (!Pip3D.isAvailable()) return false;
  // eslint-disable-next-line no-console
  console.log("[live-scan] stopPip → NexPip3D.stop()");
  return Pip3D.stop();
}

// ---------- Diagnostics ----------

export async function getPipDiagnostics(): Promise<PipDiagnostics | null> {
  if (!Pip3D.isAvailable()) return null;
  const d = await Pip3D.getDiagnostics();
  return adapt3DtoLegacy(d);
}

export function subscribePipState(cb: (diag: PipDiagnostics) => void): () => void {
  return Pip3D.subscribeState((d) => {
    const adapted = adapt3DtoLegacy(d);
    if (adapted) cb(adapted);
  });
}

/** HUD button taps from the Android card (2026-09-29). */
export function subscribePipActions(cb: (a: import("./nexPip3D").NexPip3DAction) => void): () => void {
  return Pip3D.subscribeActions(cb);
}

export function subscribePipEvents(
  cb: (evt: { event: string; snapshot: PipDiagnostics }) => void,
): () => void {
  return Pip3D.subscribeEvents((e) => {
    const adapted = adapt3DtoLegacy(e.snapshot);
    if (adapted) cb({ event: e.event, snapshot: adapted });
  });
}

/**
 * Format a one-line, user-facing PiP status string.
 */
export function describePipStatus(diag: PipDiagnostics | null): string {
  if (!diag) return "Nex PiP: status unavailable";
  if (!diag.isSupported) return "Nex PiP: not supported on this device";
  if (diag.isActive) return "Nex PiP: active (window floating)";
  const e = diag.lastDelegateEvent || "none";
  if (e.startsWith("failedToStartPiP")) {
    return `Nex PiP: failed, ${e.slice("failedToStartPiP:".length) || "see Console [NexPip3D]"}`;
  }
  if (e === "willStartPiP") return "Nex PiP: starting…";
  if (diag.isPossible) return "Nex PiP: ready (auto starts on app swap)";
  return `Nex PiP: warming layer (${diag.sampleBuffersRendered} frames rendered)…`;
}

// ---------- Legacy convenience surface ----------

export async function setStatus(status: NexPipStatus): Promise<boolean> {
  return updatePipStatus(status);
}


export function reset(): void {
  _firedThisSession = false;
  _lastPostedAt = 0;
  if (Pip3D.isAvailable()) {
    void Pip3D.stop();
  }
}

export function __hasFiredThisSession(): boolean {
  return _firedThisSession;
}

// ---------- Dex-scan PiP surface (folded from dexLivePip.ts in PR-F) ----------
//
// Pokédex-only PiP guidance copy. Maps each Dex phase onto the native
// "reading" phase key (the native renderer pairs that with the dex-tuned glow);
// the body line is what the user actually reads. Strictly Dex vocabulary — no
// "row", "slot", "box", "CP", or "captured Pokémon" language.

export type DexLivePipPhase =
  | "idle"
  | "open_pokedex"
  | "scanning"
  | "start_scrolling"
  | "hold_steady"
  | "scroll_slower"
  | "reading_grid"
  | "low_confidence"
  | "saved";

export interface DexLivePipMessage {
  phase: DexLivePipPhase;
  /** Optional override for the body line — defaults to the canonical copy. */
  message?: string;
  /** Optional sub-line under the body. */
  detail?: string;
  /** Override the bubble colour; inferred from `phase` when omitted. */
  bubble?: "green" | "yellow" | "orange" | "red";
  /**
   * R14 FIX 2 — the DISTINCT APPLIED row count for this session (the same
   * number as the final `applied:N` the user sees on the "Dex updated" card),
   * NOT a cumulative tally such as the `write veto overridden` event count
   * (134 in the R13 trace) or summed per-frame `apply observations`. Forwarded
   * to the native HUD's `captured` field so the PiP status carries the true
   * applied count as a structured number. Display-only — feeding this never
   * changes what is applied. Omit on phases with no meaningful count yet.
   */
  appliedCount?: number;
  /**
   * DETAIL-PAGE MODE. When the live scan is parked on a species detail page,
   * the HUD stops showing "which dex numbers did I just add" (meaningless —
   * the whole screen is ONE species) and instead mirrors the page: a species
   * line plus the variant cards' own labels in their own on-screen positions.
   *
   * `detailSpecies` is the header line, e.g. `Bulbasaur (#001)`.
   * `detailVariantSlots` is exactly 12 entries laid out 4x3, where "" renders
   * BLANK — the variant is not caught, or the card was not readable on this
   * frame (the extractor omits cards it could not see rather than asserting
   * them missing, so both cases are honestly "nothing to show here").
   *
   * Both are STICKY natively, like `recentDexNumbers`: omitting them leaves
   * whatever the HUD is showing, and sending an empty array clears the detail
   * layout and falls back to the dex-number grid. Display-only — the HUD never
   * decides what gets written.
   */
  detailSpecies?: string;
  detailVariantSlots?: string[];
  /**
   * HUD shape (2026-09-07): "portrait" for a Pokémon scan, "landscape" for
   * the grid. Sticky when omitted. See NexPip3DStatus.orientation.
   */
  orientation?: "landscape" | "portrait";
  /**
   * Off the Pokédex (2026-09-08): send the Scanned grid EMPTY for this push so
   * a catch screen or the friends list does not wear the last grid's numbers.
   * The rolling store is untouched; the next on-grid push shows it again.
   */
  hideRecent?: boolean;
  /**
   * SPLIT-LAYOUT fact lines (2026-08-31): the My-Pokémon HUD's left column
   * when the target visual owns the right half. One string per line, the
   * LAST entry drawn larger (the overall standing). Same sticky contract as
   * detailVariantSlots: omit = leave, empty array = clear (back to the
   * packed-grid layout). Display-only.
   */
  heroLines?: string[];
  /**
   * Top-1% league ball for the MY-POKÉMON detail HUD (2026-08-21 ask): when
   * the caught Pokémon ranks >= 99% in a league, the native HUD draws that
   * league's ball (the same glyph as the list page's dot-rail badge) in the
   * panel's top-right corner — the space the lettermark watermark otherwise
   * leaves idle. Sticky natively like `ctaText`: omit to leave the current
   * badge alone, pass "" to clear it.
   */
  leagueBadge?: "great" | "ultra" | "master" | "";
  /**
   * HERO BAND for the MY-POKÉMON detail HUD (2026-08-26 ask), drawn between
   * the species headline and the fact grid at a LARGER size than the grid:
   *   pvpLine — the league percents ("GL 51.2% · UL 92.3% · ML 88.9%").
   *   evoLine — the evolution-potential callout in the brand green
   *             ("EVOLVE → ARCANINE · #1 GL").
   * Sticky natively like `ctaText`: omit to leave, pass "" to clear.
   */
  pvpLine?: string;
  evoLine?: string;
  /**
   * TARGET VISUAL for the My-Pokémon detail HUD (2026-08-30 ask): the best
   * form this IV triple can become, drawn natively as art + league ball +
   * oversized rank instead of a sentence. Sticky natively: omit to leave,
   * "" / 0 to clear. targetName captions an evolution target only.
   *
   * targetPercent is the percent-of-best the pick was made on, pre-formatted
   * ("97.6%"). It rides in the caption because the pick is DECIDED on percent
   * (lib/bestPotential), and a claim that hides the number it was made on is
   * how a correct Master pick came to look like a mistake next to a higher
   * Great-League percent belonging to a different Pokémon.
   */
  targetSpriteUrl?: string;
  targetLeague?: "great" | "ultra" | "master" | "";
  targetRank?: number;
  targetName?: string;
  targetPercent?: string;
  /**
   * 2026-09-18: the backdrop drawn behind the target art, GO's own visual
   * language (shadow flames, lucky rays, purified glow). Sticky natively:
   * omit to leave, "" to clear.
   */
  targetAura?: "shadow" | "lucky" | "purified" | "dynamax" | "";
  /**
   * 2026-09-29 (Android card buttons): the My Pokémon record on the card,
   * and whether it is starred / on the trade list, so the star and trade
   * buttons draw their state. Sticky natively; "" clears the id.
   */
  recordId?: string;
  recordStarred?: boolean;
  recordListed?: boolean;
  /**
   * 2026-09-03: every league sharing the target's exact standing (one ball
   * each under the art; the standing stated once), and the moves read off
   * the page as pills. Both sticky: omit to leave, [] to clear.
   */
  targetLeagues?: string[];
  movePills?: string[];
  /** Hex per pill, index for index with movePills (Android paints them). */
  movePillColors?: string[];
  /**
   * 2026-09-22 (Android HUD): IV percent for the border colour (0 clears),
   * which badge face to show ("ivs" | "moves" | ""), the recommended moves
   * ([] clears) and a top-5 showcase standing (rank 0 / "" clears). All
   * sticky natively like the target fields; iOS ignores them.
   */
  ivPercent?: number;
  hudFocus?: "ivs" | "moves" | "";
  moveRecs?: string[];
  showcaseRank?: number;
  showcaseLabel?: string;
  showcaseScore?: string;
  /** 2026-09-22 (Android HUD): wild-encounter IV range; encounterCp 0 clears. */
  encounterName?: string;
  encounterCp?: number;
  encounterIvMin?: number;
  encounterIvMax?: number;
  encounterLevelMin?: number;
  encounterLevelMax?: number;
  encounterSpreads?: number;
  /**
   * Upsell call-to-action, drawn in the neon accent in the caption slot and
   * suppressing the grid beneath it. Pass "" to clear.
   *
   * A PiP overlay is a rendered video layer, NOT an interactive view, so this is
   * a prompt rather than a button — tapping the PiP is the system's restore
   * affordance and hands the user back to the app, which is what the copy asks
   * for. Sticky natively: omit to leave the current CTA alone.
   */
  ctaText?: string;
}

// THE HUD NUMBER GRID: a MIRROR MAP of the Pokédex section on screen.
//
// History: PR AA (a rolling 10-number "Added" window), PR-E30 (12 numbers,
// 4x3), 2026-08-31 (numbers the dex already had as dark chips), 2026-09-24
// morning (row-shaped: one HUD row per Pokédex row, uncaught numbers in red /
// orange-red). Owner direction the same afternoon: four rows (16 cells) that
// MIRROR the screen. The scan builds a map of every row it has read on this
// dex; the HUD shows the four rows starting at the top row on screen, so it
// scrolls with the trainer, and scrolling back up shows those rows again with
// everything learnt about them. The HUD stops being a list of new numbers and
// becomes the map of the region scanned.
//
// Chip kinds (what the scan knows about each number):
//   added   = this scan registered it (near-white chip, green ink)
//   already = the dex already had it (dark green chip, white text)
//   reading = read as caught, still being confirmed (grey chip)
//   seen    = a silhouette: seen, not caught (orange-red chip)
//   missing = a blank card: not caught (red chip)
// Rows are matched across frames by the numbers they share. Module-level (not
// React state) so every `setDexPipPhase` push carries the latest map.
const DEX_HUD_COLUMNS = 4;
const DEX_HUD_ROWS = 4;
/** Rows remembered per dex before the least recently read go. */
const DEX_MAP_MAX_ROWS = 400;
export type DexRecentKind = "added" | "already" | "reading" | "seen" | "missing";
/** Ordering of chip kinds for mergeKind: among caught shades the stronger
 * stays (a number announced as new stays new, a confirmed catch never falls
 * back to "reading"), and a silhouette never drops back to blank on one
 * flickery frame; a fresh not-caught read replaces any caught shade. */
const KIND_RANK: Record<DexRecentKind, number> = { missing: 0, seen: 1, reading: 2, already: 3, added: 4 };

/** One number the scan handled on a grid frame. `slot` is its 0-based lattice
 * index on screen (row-major, `columns` wide); without it the number joins the
 * newest row that has room. */
export interface DexHudCell {
  n: number;
  kind: DexRecentKind;
  slot?: number | null;
}

type HudChip = { n: number; kind: DexRecentKind };
interface HudRow {
  cells: Array<HudChip | null>;
  /** Recency: bumped every time a frame reads the row. */
  seq: number;
}
let _dexRows: HudRow[] = [];
let _dexRowSeq = 0;
let _dexRowContext: string | null = null;
/** The rows the latest grid frame had on screen, top to bottom. */
let _visibleRows: HudRow[] = [];

function validDex(n: number): number | null {
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}

function rowMin(row: HudRow): number {
  let min = Number.POSITIVE_INFINITY;
  for (const c of row.cells) if (c && c.n < min) min = c.n;
  return min;
}

function removeNumberFromRows(n: number, except: HudRow | null): HudChip | null {
  let removed: HudChip | null = null;
  for (const row of _dexRows) {
    if (row === except) continue;
    for (let i = 0; i < row.cells.length; i++) {
      const c = row.cells[i];
      if (c && c.n === n) {
        removed = removed && KIND_RANK[removed.kind] >= KIND_RANK[c.kind] ? removed : c;
        row.cells[i] = null;
      }
    }
  }
  return removed;
}

function strongerKind(a: DexRecentKind, b: DexRecentKind | undefined): DexRecentKind {
  return b != null && KIND_RANK[b] > KIND_RANK[a] ? b : a;
}

/**
 * Merge a fresh read (`next`) into what the map showed (`prior`). The HUD
 * shows what the scanner SEES (owner direction 2026-09-24), so a fresh
 * not-caught read (seen / missing) always replaces a caught shade: the red
 * chip is how the trainer spots a Nexus Dex entry the screen disagrees with.
 * Among caught shades the stronger stays (a registered catch never falls back
 * to "reading"), and a silhouette never drops back to blank on one frame.
 */
function mergeKind(next: DexRecentKind, prior: DexRecentKind | undefined): DexRecentKind {
  if (prior == null) return next;
  const nextCaught = KIND_RANK[next] >= KIND_RANK.reading;
  const priorCaught = KIND_RANK[prior] >= KIND_RANK.reading;
  if (!nextCaught && priorCaught) return next;
  return strongerKind(next, prior);
}

function prune(): void {
  _dexRows = _dexRows.filter((r) => r.cells.some((c) => c != null));
  _visibleRows = _visibleRows.filter((r) => _dexRows.includes(r));
  if (_dexRows.length > DEX_MAP_MAX_ROWS) {
    const keep = new Set(
      [..._dexRows].sort((a, b) => b.seq - a.seq).slice(0, DEX_MAP_MAX_ROWS),
    );
    _dexRows = _dexRows.filter((r) => keep.has(r));
    _visibleRows = _visibleRows.filter((r) => keep.has(r));
  }
}

/**
 * Record every number one grid frame handled. `context` names the dex being
 * swept (variant + region); a change starts a fresh map so a Shiny row never
 * sits between two Main rows. `columns` is the page's lattice width (3 on
 * G-Max, else 4).
 */
export function noteDexFrameCells(
  cells: DexHudCell[],
  opts: { columns?: number; context?: string | null } = {},
): void {
  const context = opts.context ?? null;
  if (context != null && context !== _dexRowContext) {
    if (_dexRowContext != null) {
      _dexRows = [];
      _visibleRows = [];
    }
    _dexRowContext = context;
  }
  const columns = Math.max(1, Math.min(DEX_HUD_COLUMNS, opts.columns ?? DEX_HUD_COLUMNS));
  // Group by on-screen row; unslotted numbers are placed after.
  const byRow = new Map<number, Array<{ n: number; kind: DexRecentKind; col: number }>>();
  const loose: HudChip[] = [];
  for (const cell of cells) {
    const n = validDex(cell.n);
    if (n == null) continue;
    if (cell.slot != null && Number.isFinite(cell.slot) && cell.slot >= 0) {
      const slot = Math.floor(cell.slot);
      const key = Math.floor(slot / columns);
      const list = byRow.get(key) ?? [];
      list.push({ n, kind: cell.kind, col: slot % columns });
      byRow.set(key, list);
    } else {
      loose.push({ n, kind: cell.kind });
    }
  }
  const touched: HudRow[] = [];
  for (const key of [...byRow.keys()].sort((a, b) => a - b)) {
    const group = byRow.get(key)!;
    const numbers = new Set(group.map((g) => g.n));
    // The remembered row that already holds any of these numbers IS this row.
    let row = _dexRows.find((r) => r.cells.some((c) => c != null && numbers.has(c.n))) ?? null;
    if (!row) {
      row = { cells: new Array<HudChip | null>(DEX_HUD_COLUMNS).fill(null), seq: 0 };
      _dexRows.push(row);
    }
    row.seq = ++_dexRowSeq;
    for (const g of group) {
      const elsewhere = removeNumberFromRows(g.n, row);
      const prior = row.cells.find((c) => c != null && c.n === g.n) ?? null;
      // The number moves to the column the frame saw it in.
      for (let i = 0; i < row.cells.length; i++) {
        if (row.cells[i]?.n === g.n) row.cells[i] = null;
      }
      const occupant = row.cells[g.col];
      // Two different numbers read into one column: this frame's read wins.
      if (occupant && occupant.n !== g.n) row.cells[g.col] = null;
      row.cells[g.col] = {
        n: g.n,
        kind: mergeKind(g.kind, prior?.kind ?? elsewhere?.kind),
      };
    }
    if (!touched.includes(row)) touched.push(row);
  }
  if (touched.length > 0) _visibleRows = touched;
  for (const chip of loose) {
    const existing = _dexRows.flatMap((r) => r.cells).find((c) => c != null && c.n === chip.n);
    if (existing) {
      existing.kind = mergeKind(chip.kind, existing.kind);
      continue;
    }
    const newest = [..._dexRows].sort((a, b) => b.seq - a.seq)[0];
    const free = newest ? newest.cells.findIndex((c) => c == null) : -1;
    if (newest && free >= 0) {
      newest.cells[free] = chip;
      newest.seq = ++_dexRowSeq;
    } else {
      const cellsArr = new Array<HudChip | null>(DEX_HUD_COLUMNS).fill(null);
      cellsArr[0] = chip;
      _dexRows.push({ cells: cellsArr, seq: ++_dexRowSeq });
    }
  }
  prune();
}

/** Newly registered numbers with no lattice slot (kept for callers and tests
 * that only know the numbers). */
export function noteDexRecentAdds(added: number[]): number[] {
  noteDexFrameCells(added.map((n) => ({ n, kind: "added" as const })));
  return getDexRecentAdds();
}

/** Numbers the dex already had, with no lattice slot. */
export function noteDexRecentSeen(seen: number[]): void {
  noteDexFrameCells(seen.map((n) => ({ n, kind: "already" as const })));
}

/** Clear the HUD map: called when a scan session resets. */
export function resetDexRecentAdds(): void {
  _dexRows = [];
  _visibleRows = [];
  _dexRowSeq = 0;
  _dexRowContext = null;
}

/**
 * The four rows the HUD shows: the map in dex order, windowed to start at the
 * top row on screen (or, before any grid frame, the most recently read row),
 * pulled up when the screen is at the end of what has been mapped so the HUD
 * stays full.
 */
function displayRows(): HudRow[] {
  const sorted = [..._dexRows].sort((a, b) => rowMin(a) - rowMin(b));
  if (sorted.length <= DEX_HUD_ROWS) return sorted;
  let anchor: HudRow | undefined = _visibleRows.length > 0
    ? [..._visibleRows].sort((a, b) => rowMin(a) - rowMin(b))[0]
    : [..._dexRows].sort((a, b) => b.seq - a.seq)[0];
  let start = anchor ? sorted.indexOf(anchor) : 0;
  if (start < 0) start = 0;
  start = Math.min(start, sorted.length - DEX_HUD_ROWS);
  return sorted.slice(start, start + DEX_HUD_ROWS);
}

/** Every number this scan REGISTERED that the map holds, most recently read
 * row first. For the guidance's "never offer a number the grid already shows"
 * set + tests. */
export function getDexRecentAdds(): number[] {
  return [..._dexRows]
    .sort((a, b) => b.seq - a.seq)
    .flatMap((r) => r.cells.filter((c): c is HudChip => c != null && c.kind === "added").map((c) => c.n).sort((a, b) => b - a));
}

/**
 * The native HUD fills its 4x4 grid from the top-left in array order, so the
 * rows go out in dex order, four slots each, with 0 holding a column that has
 * no number (a blank the HUD draws as nothing). Exported for tests.
 */
export function dexRecentNumbersForPip(): number[] {
  return displayRows().flatMap((r) => r.cells.map((c) => (c ? c.n : 0)));
}

/** The per-cell kinds aligned index-for-index with dexRecentNumbersForPip()
 * ("" under a blank slot). Rides the same push; the native HUD colours each
 * chip from it. */
export function dexRecentKindsForPip(): Array<DexRecentKind | ""> {
  return displayRows().flatMap((r) => r.cells.map((c) => (c ? c.kind : "")));
}

const DEX_PIP_COPY: Record<DexLivePipPhase, string> = {
  // 2026-09-03: the scan reads BOTH the Pokédex grid and a Pokémon's own
  // screen, so the standby copy names both (owner ask).
  idle: "Open a Pokédex or Pokémon screen",
  open_pokedex: "Open a Pokédex or Pokémon screen",
  // Live: frames ARE arriving but the classifier isn't recognising a Pokémon GO
  // dex grid or Pokémon screen yet (still in Nexus, on the map, or another
  // screen → frames drop as nexus_dex_ui). Direct the user onto a readable
  // screen instead of a misleading "Scanning…". Distinct wording from
  // open_pokedex so the pill visibly changes when the loop goes live,
  // confirming the scanner is running.
  scanning: "Show a Pokédex or Pokémon screen",
  // On a recognised dex page but nothing read yet — the grid is static, so the
  // scanner needs motion. Tell the user to scroll rather than implying progress.
  start_scrolling: "Start scrolling slowly to scan",
  hold_steady: "Hold steady while entries confirm",
  scroll_slower: "Scroll slower",
  reading_grid: "Scanning…",
  low_confidence: "Hold steadier",
  saved: "Dex updated",
};

const DEX_PIP_BUBBLE: Record<DexLivePipPhase, "green" | "yellow" | "orange" | "red"> = {
  idle: "yellow",
  open_pokedex: "yellow",
  scanning: "yellow",
  start_scrolling: "yellow",
  hold_steady: "yellow",
  scroll_slower: "orange",
  reading_grid: "green",
  low_confidence: "orange",
  saved: "green",
};

/**
 * Map a Dex-scan phase onto the native NexPip3D phase vocabulary. b13c.4: the
 * call below previously hardcoded `phase: "reading"` for EVERY dex update, so
 * the native trace always logged `phase=reading` regardless of where the JS
 * state machine actually was, and the native status dot / glow was stuck on the
 * yellow "HOLD" cue even while actively scanning. We always pass our own
 * `message` so the body copy is correct either way, but forwarding the real
 * phase makes the native dot colour and the device trace reflect the live
 * state. The native plugin understands "scanning" / "idle" / "finished"
 * directly and falls back to a yellow HUD for anything else.
 */
function dexPhaseToNative(phase: DexLivePipPhase): Pip3D.NexPip3DPhase {
  switch (phase) {
    case "idle":
      return "idle";
    case "open_pokedex":
      return "open_pogo";
    case "scanning":
    case "reading_grid":
      return "scanning";
    case "saved":
      return "finished";
    // start_scrolling / hold_steady / scroll_slower / low_confidence have no
    // exact native phase; "reading" gives them the yellow "hold" cue, which
    // matches their guidance intent.
    case "start_scrolling":
    case "hold_steady":
    case "scroll_slower":
    case "low_confidence":
    default:
      return "reading";
  }
}

/**
 * Send a Dex-only PiP status update. Safe on web (the bridge logs to console).
 */
/** Phases that mean "not on a Pokédex or Pokémon screen": the HUD shows no dex chips for them. */
const OFF_DEX_PHASES: ReadonlySet<DexLivePipPhase> = new Set<DexLivePipPhase>(["idle", "open_pokedex", "scanning"]);

export async function setDexPipPhase(msg: DexLivePipMessage): Promise<boolean> {
  const body = msg.message ?? DEX_PIP_COPY[msg.phase];
  const bubble = msg.bubble ?? DEX_PIP_BUBBLE[msg.phase];
  const nativePhase = dexPhaseToNative(msg.phase);
  // b13c.4: confirm the phase + message actually being pushed to native. The
  // build-193 trace showed `msg=Open your Pokédex` while the JS state machine
  // was in phase:"scanning" — this log at the single call site proves which
  // (phase, message) pair each updateStatus carries on the next device test.
  // eslint-disable-next-line no-console
  if (DEX_SCAN_VERBOSE) console.log("[dex-scan] pip update", { phase: msg.phase, nativePhase, message: body });
  return Pip3D.updateStatus({
    phase: nativePhase,
    message: body,
    detail: msg.detail,
    bubble,
    target: 1, // suppress the "captured 12" progress chip
    // R14 FIX 2 — carry the DISTINCT APPLIED row count through the PiP status as
    // a structured number (not just embedded in the `detail` string), so the
    // native HUD / diagnostics read the same value the user sees on the final
    // "Dex updated" card instead of a cumulative override/observation tally. The
    // chip itself stays suppressed by target:1, so this is display-data only and
    // never alters apply behaviour. Default 0 keeps a count-less push explicit.
    captured: msg.appliedCount ?? 0,
    // PR AA: ride the rolling dex grid along on every dex PiP update so the
    // native HUD's 4x3 number grid stays current as OCR streams results.
    // Four slots per Pokédex row, rows in dex order, 0 = a blank column (see
    // dexRecentNumbersForPip).
    // An off-dex phase (nothing recognised: the map, a sheet, another app)
    // never wears the last grid's chips (2026-09-22 Galaxy photos: the map
    // showed "Show a Pokédex or Pokémon screen" with three dex chips under
    // it, because the drop path pushed the sentence without hideRecent).
    recentDexNumbers: msg.hideRecent || OFF_DEX_PHASES.has(msg.phase) ? [] : dexRecentNumbersForPip(),
    recentDexKinds: msg.hideRecent || OFF_DEX_PHASES.has(msg.phase) ? [] : dexRecentKindsForPip(),
    // Detail-page mode. Only sent when the caller supplied them, so a normal
    // grid-scan push leaves the native detail layout untouched (sticky) exactly
    // as it does for the clinical fields — and a detail push that omits them
    // never wipes a species line mid-dwell.
    ...(msg.detailSpecies !== undefined ? { detailSpecies: msg.detailSpecies } : {}),
    ...(msg.heroLines !== undefined ? { heroLines: msg.heroLines } : {}),
    ...(msg.detailVariantSlots !== undefined
      ? { detailVariantSlots: msg.detailVariantSlots }
      : {}),
    ...(msg.leagueBadge !== undefined ? { leagueBadge: msg.leagueBadge } : {}),
    ...(msg.pvpLine !== undefined ? { pvpLine: msg.pvpLine } : {}),
    ...(msg.evoLine !== undefined ? { evoLine: msg.evoLine } : {}),
    ...(msg.targetSpriteUrl !== undefined ? { targetSpriteUrl: msg.targetSpriteUrl } : {}),
    ...(msg.targetLeague !== undefined ? { targetLeague: msg.targetLeague } : {}),
    ...(msg.targetRank !== undefined ? { targetRank: msg.targetRank } : {}),
    ...(msg.targetName !== undefined ? { targetName: msg.targetName } : {}),
    ...(msg.targetPercent !== undefined ? { targetPercent: msg.targetPercent } : {}),
    ...(msg.targetAura !== undefined ? { targetAura: msg.targetAura } : {}),
    ...(msg.recordId !== undefined ? { recordId: msg.recordId } : {}),
    ...(msg.recordStarred !== undefined ? { recordStarred: msg.recordStarred } : {}),
    ...(msg.recordListed !== undefined ? { recordListed: msg.recordListed } : {}),
    ...(msg.targetLeagues !== undefined ? { targetLeagues: msg.targetLeagues } : {}),
    ...(msg.movePills !== undefined ? { movePills: msg.movePills } : {}),
    ...(msg.movePillColors !== undefined ? { movePillColors: msg.movePillColors } : {}),
    ...(msg.ivPercent !== undefined ? { ivPercent: msg.ivPercent } : {}),
    ...(msg.hudFocus !== undefined ? { hudFocus: msg.hudFocus } : {}),
    ...(msg.moveRecs !== undefined ? { moveRecs: msg.moveRecs } : {}),
    ...(msg.showcaseRank !== undefined ? { showcaseRank: msg.showcaseRank } : {}),
    ...(msg.showcaseLabel !== undefined ? { showcaseLabel: msg.showcaseLabel } : {}),
    ...(msg.showcaseScore !== undefined ? { showcaseScore: msg.showcaseScore } : {}),
    ...(msg.encounterName !== undefined ? { encounterName: msg.encounterName } : {}),
    // The wild encounter is STICKY between pushes (2026-09-30 Galaxy run):
    // the estimate showed for one frame and vanished, because every grid,
    // scroll-skip and blur push that followed carried an implied
    // encounterCp: 0. The native HUD expires it on its own clock instead
    // (NexPip3D ENCOUNTER_TTL_MS after the last encounter read, with a
    // scheduled expiry so the map after a catch never keeps it: the
    // 2026-09-22 "lingered" report was the TTL only being checked on the
    // next push). A Pokémon or species card still clears it explicitly.
    ...(msg.encounterCp !== undefined ? { encounterCp: msg.encounterCp } : {}),
    ...(msg.encounterIvMin !== undefined ? { encounterIvMin: msg.encounterIvMin } : {}),
    ...(msg.encounterIvMax !== undefined ? { encounterIvMax: msg.encounterIvMax } : {}),
    ...(msg.encounterLevelMin !== undefined ? { encounterLevelMin: msg.encounterLevelMin } : {}),
    ...(msg.encounterLevelMax !== undefined ? { encounterLevelMax: msg.encounterLevelMax } : {}),
    ...(msg.encounterSpreads !== undefined ? { encounterSpreads: msg.encounterSpreads } : {}),
    ...(msg.ctaText !== undefined ? { ctaText: msg.ctaText } : {}),
    ...(msg.orientation !== undefined ? { orientation: msg.orientation } : {}),
  });
}

/** Reset the clinical HUD overlays so stale traffic-light cues don't linger. */
export async function clearDexPipClinical(): Promise<void> {
  await Pip3D.clearClinical().catch(() => false);
}

/**
 * Documented limitation surface (carried from dexLivePip.ts): the native
 * NexPip3D renderer is procedural, so bitmap injection (idle splash image)
 * would need a Swift change. Dex PiP ships text-only messages.
 */
export const dexPipIdleSplashLimitation =
  "The native NexPip3D renderer is procedural, bitmap injection (idle " +
  "splash image) would need a Swift change. Dex PiP ships text-only " +
  "messages; image splash is a follow-up build.";
